import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PALETTES, FONT_PAIRS, VARIANTS } from "@/lib/design";
import { Row } from "./_ui";
import LogoStudio from "./panels/LogoStudio";
import ThemePickerPanel from "./panels/ThemePickerPanel";
import RegenerateLookPanel from "./panels/RegenerateLookPanel";
import { WireframeSelect, HEADER_ITEMS, FOOTER_ITEMS } from "./panels/LayoutWireframes";

/**
 * Design tab — everything that controls how the public site LOOKS:
 *   • Visual theme (header / footer / layout / palette / font + Shuffle + Presets)
 *   • Template / variant / palette / font_pair dropdown power-user fallback
 *   • Site logo (LogoStudio)
 *   • Header style — variant + sticky + date + weather + subscribe CTA + breaking ticker
 *   • Footer style — variant + brand text + newsletter signup toggle
 *
 * Operational config (status, retention, AdSense, social handles, custom HTML,
 * About text, etc.) lives in the sibling SettingsTab so neither tab is a
 * neverending scroll.
 */

// Build the form state from the live `site` doc. Centralised so the initial
// mount value and the re-sync effect can't drift apart.
function formFromSite(site) {
  return {
    template: site.template,
    variant: site.variant,
    palette: site.palette,
    font_pair: site.font_pair,
    header_variant: site.header_variant || "minimal",
    header_sticky: !!site.header_sticky,
    header_show_date: site.header_show_date !== false,
    header_show_weather: !!site.header_show_weather,
    header_weather_location: site.header_weather_location || "",
    header_subscribe_url: site.header_subscribe_url || "",
    header_subscribe_label: site.header_subscribe_label || "Subscribe",
    breaking_news_enabled: !!site.breaking_news_enabled,
    breaking_news_label: site.breaking_news_label || "Breaking news",
    footer_variant: site.footer_variant || "minimal",
    footer_description: site.footer_description || "",
    footer_newsletter_enabled: site.footer_newsletter_enabled !== false,
  };
}

export default function DesignTab({ site, reload }) {
  const [form, setForm] = useState(() => formFromSite(site));
  // When the Theme Picker (or any external action) updates the site doc and
  // calls reload(), the parent re-renders DesignTab with a fresh `site` prop.
  // Without this effect, our local `form` state stays anchored to the values
  // captured at mount time — so clicking "Save" at the bottom would POST the
  // OLD values and overwrite the just-applied change. Re-sync on every site
  // update so the form stays a faithful mirror of the DB.
  useEffect(() => {
    setForm(formFromSite(site));
  }, [site]); // re-sync whenever the parent reloads the site object

  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [saveError, setSaveError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true); setSaveStatus(""); setSaveError("");
    try {
      await api.patch(`/sites/${site.id}`, form);
      await reload();
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(""), 4000);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.detail) || "Save failed — check console for details.";
      setSaveStatus("error");
      setSaveError(String(msg));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Auto-generator — one-click 3 candidate cards */}
      <RegenerateLookPanel site={site} reload={reload} />

      {/* Visual Theme — instant-apply panel; no Save needed for these */}
      <div className="pt-4 label-tiny">Visual Theme (dimension-by-dimension)</div>
      <ThemePickerPanel site={site} reload={reload} />

      {/* Power-user dropdowns — same fields the Theme Picker writes, but
          presented as a compact one-glance view for operators who already
          know what they want. */}
      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Quick selects</div>
      <Row label="Template / Variant">
        <div className="flex gap-2">
          <select value={form.template} onChange={(e) => set("template", e.target.value)} className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm">
            {["tribune", "terminal", "brief", "newsmag"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <select value={form.variant} onChange={(e) => set("variant", e.target.value)} className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm">
            {(VARIANTS[form.template] || []).map((v) => <option key={v.key} value={v.key}>{v.name}</option>)}
          </select>
        </div>
      </Row>
      <Row label="Palette">
        <select value={form.palette} onChange={(e) => set("palette", e.target.value)} className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm">
          {Object.entries(PALETTES).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
        </select>
      </Row>
      <Row label="Font pair">
        <select value={form.font_pair} onChange={(e) => set("font_pair", e.target.value)} className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm">
          {Object.entries(FONT_PAIRS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
        </select>
      </Row>

      {/* Logo */}
      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Site logo</div>
      <Row label="Logo"><LogoStudio site={site} reload={reload} /></Row>

      {/* Header */}
      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Header style</div>
      <Row label="Variant">
        <WireframeSelect
          kind="header"
          items={HEADER_ITEMS}
          value={form.header_variant}
          onChange={(v) => set("header_variant", v)}
          testId="sitedetail-header-variant"
        />
      </Row>
      <Row label="Sticky on scroll">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.header_sticky} onChange={(e) => set("header_sticky", e.target.checked)} data-testid="sitedetail-header-sticky" />
          <span className="text-sm">{form.header_sticky ? "ON — header sticks to top on scroll" : "OFF"}</span>
        </label>
      </Row>
      <Row label="Show date">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.header_show_date} onChange={(e) => set("header_show_date", e.target.checked)} data-testid="sitedetail-header-date" />
          <span className="text-sm">{form.header_show_date ? "ON — show current date in utility bar" : "OFF"}</span>
        </label>
      </Row>
      <Row label="Show weather">
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.header_show_weather} onChange={(e) => set("header_show_weather", e.target.checked)} data-testid="sitedetail-header-weather" />
            <span className="text-sm">{form.header_show_weather ? "ON — show small weather widget" : "OFF"}</span>
          </label>
          {form.header_show_weather && (
            <input
              value={form.header_weather_location}
              onChange={(e) => set("header_weather_location", e.target.value)}
              placeholder="e.g. New York / London / Tokyo"
              className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
            />
          )}
        </div>
      </Row>
      <Row label="Subscribe CTA">
        <div className="space-y-2">
          <input
            value={form.header_subscribe_url}
            onChange={(e) => set("header_subscribe_url", e.target.value)}
            placeholder="Leave blank for built-in /subscribe page (recommended)"
            className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
          />
          <input
            value={form.header_subscribe_label}
            onChange={(e) => set("header_subscribe_label", e.target.value)}
            placeholder="Subscribe"
            className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
          />
        </div>
      </Row>
      <Row label="Breaking news ticker">
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.breaking_news_enabled} onChange={(e) => set("breaking_news_enabled", e.target.checked)} data-testid="sitedetail-breaking-news" />
            <span className="text-sm">{form.breaking_news_enabled ? "ON — animated ticker with 5 latest articles below header" : "OFF"}</span>
          </label>
          {form.breaking_news_enabled && (
            <input
              value={form.breaking_news_label}
              onChange={(e) => set("breaking_news_label", e.target.value)}
              placeholder="Breaking news"
              className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
            />
          )}
        </div>
      </Row>

      {/* Footer */}
      <div className="pt-6 mt-6 border-t border-zinc-800 label-tiny">Footer style</div>
      <Row label="Variant">
        <div className="space-y-2">
          <WireframeSelect
            kind="footer"
            items={FOOTER_ITEMS}
            value={form.footer_variant}
            onChange={(v) => set("footer_variant", v)}
            testId="sitedetail-footer-variant"
          />
          <div className="text-[11px] text-zinc-500 mono">
            Save and refresh the public site to see the new layout. Footer adapts to your palette + accent color automatically.
          </div>
        </div>
      </Row>
      <Row label="Footer brand text">
        <textarea
          rows={2}
          value={form.footer_description}
          onChange={(e) => set("footer_description", e.target.value)}
          placeholder="e.g. Where voices unite, stories flourish, and community thrives through open dialogue and meaningful connections."
          data-testid="sitedetail-footer-description"
          className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
        />
      </Row>
      <Row label="Newsletter signup">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.footer_newsletter_enabled} onChange={(e) => set("footer_newsletter_enabled", e.target.checked)} data-testid="sitedetail-footer-newsletter" />
          <span className="text-sm">{form.footer_newsletter_enabled ? "ON — show signup form in footer (Navy Brand variant)" : "OFF — hide signup form"}</span>
        </label>
      </Row>

      <div className="pt-4 flex items-center gap-3">
        <button
          data-testid="sitedetail-save-design"
          onClick={save}
          disabled={busy}
          className="bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-5 py-2 rounded-sm text-sm font-semibold"
        >
          {busy ? "Saving…" : "Save Design"}
        </button>
        {saveStatus === "saved" && (
          <span data-testid="sitedetail-design-save-success" className="text-xs text-emerald-400 mono">
            ✓ Saved — changes applied
          </span>
        )}
        {saveStatus === "error" && (
          <span data-testid="sitedetail-design-save-error" className="text-xs text-rose-400 mono">
            ✕ {saveError}
          </span>
        )}
      </div>
    </div>
  );
}
